import { useMemo } from "react";
import { objectTypes, actionNames } from "@newsela/snitch";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { useStandardsSearchContext } from "@/search/Langston/StandardsSearchProvider";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { STANDARDS_SET_MODE } from "@/search/Langston/types/IStandardsSetMode";
import { useStandardsModalContext } from "@/search/Langston/FilterPanel/filters/Standards/StandardsModalProvider";

interface TrackingProperties {
  [key: string]: string;
}

export const useStandardsModalFooter = (
  setStandardsModalActive: (val: boolean) => void,
) => {
  const { getTemporarySelectedFilters } = useStandardsSearchContext();
  const {
    handlers: {
      appendSearchParam,
      deleteSingleParamValue,
      getSearchParamValues,
    },
  } = useSearchPageContext();
  const {
    getters: {
      standardsSetMode,
      newStandardsSet: { title: newTitle, children: newStandardsInSet },
      isEditingSet,
      isConfirmActionModalOpen,
      draftStandardsSetId,
      isPublishingStandardsSet,
    },
  } = useStandardsModalContext();

  const handleApplyFilters = () => {
    const selectedStandards = getTemporarySelectedFilters();

    const standardsInQuery = getSearchParamValues(
      LangstonParameterSchema.standards.key,
    );
    const newStandardsToAppend = selectedStandards.filter(
      (stnd) => !standardsInQuery.includes(stnd.id),
    );
    newStandardsToAppend.forEach((standard) => {
      appendSearchParam(LangstonParameterSchema.standards.key, standard.id);
    });

    const standardsToRemove =
      standardsInQuery && standardsInQuery.length
        ? standardsInQuery.filter(
            (id: string) => !selectedStandards.some((stnd) => stnd.id === id),
          )
        : [];

    standardsToRemove.forEach((id: string) => {
      deleteSingleParamValue(LangstonParameterSchema.standards.key, id);
    });

    setStandardsModalActive(false);
  };

  const getConfirmButtonText = () => {
    if (standardsSetMode === STANDARDS_SET_MODE.CREATE) {
      return "Create standard set";
    } else if (standardsSetMode === STANDARDS_SET_MODE.EDIT) {
      return "Update set";
    } else {
      return "Apply filters";
    }
  };

  const confirmButtonText = getConfirmButtonText();

  const dataQaSelectors = {
    cancel:
      standardsSetMode === STANDARDS_SET_MODE.CREATE
        ? "cancel-new-standards-set-filter-button"
        : "cancel-standards-filter-button",
    confirm:
      standardsSetMode === STANDARDS_SET_MODE.CREATE
        ? "create-new-standards-set-button"
        : "apply-standards-filter-button",
  };

  const trackingProperties: TrackingProperties = {
    [STANDARDS_SET_MODE.CREATE]: "Create new standards set",
    [STANDARDS_SET_MODE.EDIT]: "Edit standard set",
    [STANDARDS_SET_MODE.VIEW]: "Apply standards to apply to search query",
  };

  const getCancelEventProperties = () => ({
    objectType: objectTypes.content,
    actionName: actionNames.cancel,
    streamProperties: {
      description: "Cancel standards search filtering",
    },
  });

  const getEventProperties = (standardsSetMode: string) => {
    return {
      objectType: objectTypes.content,
      actionName: actionNames.create,
      streamProperties: {
        description: trackingProperties[standardsSetMode] || "",
      },
    };
  };

  const isConfirmButtonDisabled = useMemo(
    () => ({
      [STANDARDS_SET_MODE.CREATE]:
        newTitle === "" ||
        newStandardsInSet.length === 0 ||
        draftStandardsSetId === "",
      [STANDARDS_SET_MODE.DELETE]: isEditingSet || isConfirmActionModalOpen,
      [STANDARDS_SET_MODE.EDIT]: false,
      [STANDARDS_SET_MODE.VIEW]: false,
    }),
    [newTitle, newStandardsInSet, isEditingSet, isConfirmActionModalOpen],
  );

  const confirmButtonLoading = useMemo(
    () => ({
      [STANDARDS_SET_MODE.CREATE]: isPublishingStandardsSet,
      [STANDARDS_SET_MODE.EDIT]: false,
      [STANDARDS_SET_MODE.DELETE]: false,
      [STANDARDS_SET_MODE.VIEW]: false,
    }),
    [isPublishingStandardsSet],
  );

  return {
    dataQaSelectors,
    getCancelEventProperties,
    getEventProperties,
    confirmButtonText,
    handleApplyFilters,
    isConfirmButtonDisabled,
    confirmButtonLoading,
  };
};
